import { Storefront as PageHeadingIcon } from '@phosphor-icons/react';
import StartupPageTitle from '../components/StartupPageTitle';
/**
 * VendorProfile.jsx — founder-facing detail page for a service or credit vendor.
 * Routes: /startup/services/:vendorId  and  /startup/credit/:vendorId
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { ArrowLeft, Check, ArrowRight, X, PaperPlaneTilt, Globe, EnvelopeSimple, Phone, Buildings, Bank, CaretDown, FileText } from '@phosphor-icons/react';
import RequiredDocsView from '../components/RequiredDocsView';
import { formatPhone } from '@/utils/phone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VendorProfile = ({ mode }) => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const isService = mode === 'service';
  const accent = isService ? '#10b981' : '#f59e0b';
  const Icon = isService ? Buildings : Bank;
  const path = isService ? 'services' : 'credit';
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [introOffering, setIntroOffering] = useState(null);   // offering object OR {} for generic
  // (Iter 47) Vault taxonomy + filled-slot set used to mark uploaded docs with ✓
  const [taxonomy, setTaxonomy] = useState([]);
  const [filledSlots, setFilledSlots] = useState(new Set());
  const [expandedDocs, setExpandedDocs] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [v, tax, vault] = await Promise.all([
          api.get(`/${path}/vendors/${vendorId}`),
          api.get('/vault/structure'),
          api.get('/vault').catch(() => ({ data: { files: [] } })),
        ]);
        if (!cancelled) {
          setVendor(v.data.vendor);
          setTaxonomy(tax.data.datarooms || []);
          setFilledSlots(new Set((vault.data.files || []).map((f) => `${f.dataroom_id}::${f.slot_id}`)));
        }
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Vendor not found');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [vendorId, mode]);

  if (loading) return <div className="p-12 text-sm text-text-muted">Loading…</div>;
  if (!vendor) return <div className="p-12 text-sm text-text-muted">Vendor not found.</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1080px] mx-auto pb-20" data-testid="vendor-profile">
      <button onClick={() => navigate(`/startup/${path}`)} className="text-xs text-text-muted hover:text-text-primary inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={11} weight="bold" /> Back to {isService ? 'Services' : 'Credit'}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Main */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            {vendor.logo_file_id ? (
              <img src={`${API}/files/${vendor.logo_file_id}/public`} alt={`${vendor.name} logo`}
                data-testid="vendor-profile-logo"
                className="w-12 h-12 rounded-lg object-cover border border-border bg-white flex-shrink-0" />
            ) : (
              <span className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${accent}1a`, color: accent }}><Icon size={20} weight="fill" /></span>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold inline-flex items-center gap-1" style={{ color: accent }}><Check size={9} weight="bold" /> Verified vendor</div>
              <StartupPageTitle icon={PageHeadingIcon} className="text-3xl font-heading font-bold text-text-primary" style={{ letterSpacing: '-0.02em' }}>{vendor.name}</StartupPageTitle>
            </div>
          </div>
          <p className="text-base text-text-secondary mb-5">{vendor.tagline}</p>

          {vendor.description && (
            <div className="bg-surface border border-border rounded-xl p-5 mb-5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-2">About</div>
              <p className="text-sm text-text-secondary whitespace-pre-wrap leading-relaxed">{vendor.description}</p>
            </div>
          )}

          {/* Categories */}
          {(isService ? vendor.service_categories : vendor.credit_products || []).length > 0 && (
            <div className="bg-surface border border-border rounded-xl p-5 mb-5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-2">{isService ? 'Service categories' : 'Credit products'}</div>
              <div className="flex flex-wrap gap-1.5">
                {(isService ? vendor.service_categories : vendor.credit_products).map((c, i) => (
                  <span key={i} className="text-xs bg-background border border-border rounded-full px-2.5 py-1 capitalize">{c.replace(/_/g, ' ')}</span>
                ))}
              </div>
            </div>
          )}

          {/* Catalog offerings — Iter 44 (with required docs from Iter 47) */}
          {(vendor.catalog_offerings || []).length > 0 && (
            <>
              <h2 className="text-lg font-heading font-bold text-text-primary mb-3" style={{ letterSpacing: '-0.01em' }}>Offerings ({vendor.catalog_offerings.length})</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5" data-testid="vendor-catalog-offerings">
                {vendor.catalog_offerings.map((o) => {
                  const reqDocs = o.required_documents || [];
                  const ready = reqDocs.filter((d) => filledSlots.has(`${d.dataroom_id}::${d.slot_id}`)).length;
                  const expanded = expandedDocs === o.catalog_item_id;
                  return (
                    <div key={o.catalog_item_id} className="bg-surface border border-border rounded-xl p-4 flex flex-col" data-testid={`vendor-catalog-offering-card-${o.catalog_item_id}`}>
                      <h3 className="text-sm font-heading font-bold text-text-primary mb-1">{o.catalog_item_name}</h3>
                      <div className="text-2xl font-mono font-bold text-text-primary mb-1">{o.currency === 'INR' ? '\u20b9' : o.currency + ' '}{(o.gross_price || 0).toLocaleString('en-IN')}</div>
                      <div className="text-[11px] text-text-muted mb-3">{o.cadence === 'one_time' ? 'one-time' : 'per ' + (o.cadence || 'one_time').replace('_', ' ')}{o.turnaround_days ? ` · ${o.turnaround_days}-day turnaround` : ''}</div>

                      {reqDocs.length > 0 && (
                        <>
                          <button onClick={() => setExpandedDocs(expanded ? null : o.catalog_item_id)} data-testid={`vendor-offering-docs-toggle-${o.catalog_item_id}`}
                            className="text-[11px] inline-flex items-center justify-between px-2.5 py-1.5 rounded border border-border bg-background hover:border-text-primary mb-2">
                            <span className="inline-flex items-center gap-1.5 text-text-secondary">
                              <FileText size={11} weight="bold" style={{ color: accent }} />
                              {reqDocs.length} doc{reqDocs.length === 1 ? '' : 's'} req&rsquo;d &middot; <strong style={{ color: ready === reqDocs.length ? accent : 'var(--text-secondary)' }}>{ready}/{reqDocs.length} ready</strong>
                            </span>
                            <CaretDown size={10} weight="bold" className="text-text-muted" style={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />
                          </button>
                          {expanded && (
                            <div className="mb-3 p-2.5 rounded border border-border bg-background" data-testid={`vendor-offering-docs-${o.catalog_item_id}`}>
                              <RequiredDocsView items={reqDocs} filledSlots={filledSlots} taxonomy={taxonomy} accent={accent} />
                            </div>
                          )}
                        </>
                      )}

                      <button onClick={() => setIntroOffering({ id: o.catalog_item_id, title: o.catalog_item_name, gross_price: o.gross_price, currency: o.currency })} data-testid={`vendor-catalog-offering-intro-${o.catalog_item_id}`} className="mt-auto inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: accent }}>
                        Request intro <ArrowRight size={9} weight="bold" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Legacy free-form offerings — kept for backward compat (hidden by default in iter 44) */}
          {(vendor.offerings || []).length > 0 && (
            <>
          <h2 className="text-lg font-heading font-bold text-text-primary mb-3" style={{ letterSpacing: '-0.01em' }}>Other offerings ({vendor.offerings.length})</h2>
          {(vendor.offerings || []).length === 0 ? (
            (vendor.catalog_offerings || []).length === 0 ? (
              <div className="border border-dashed border-border rounded-xl p-8 text-center text-sm text-text-muted">No public offerings yet — request an intro to learn more.</div>
            ) : null
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5" data-testid="vendor-offerings">
              {vendor.offerings.map((o) => (
                <div key={o.id} className="bg-surface border border-border rounded-xl p-4 flex flex-col" data-testid={`vendor-offering-card-${o.id}`}>
                  <h3 className="text-sm font-heading font-bold text-text-primary mb-1">{o.title}</h3>
                  <div className="text-2xl font-mono font-bold text-text-primary mb-1">{o.currency} {(o.gross_price || 0).toLocaleString('en-IN')}</div>
                  <div className="text-[11px] text-text-muted mb-2">{o.duration_label || 'one-time'}</div>
                  {o.description && <p className="text-xs text-text-secondary mb-3 flex-1">{o.description}</p>}
                  {(o.deliverables || []).length > 0 && (
                    <ul className="space-y-0.5 text-[11px] text-text-secondary mb-3">
                      {o.deliverables.map((d, i) => <li key={i} className="inline-flex items-start gap-1.5"><span className="text-success mt-0.5">\u2713</span> {d}</li>)}
                    </ul>
                  )}
                  <button onClick={() => setIntroOffering(o)} data-testid={`vendor-offering-intro-${o.id}`} className="mt-auto inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: accent }}>
                    Request intro <ArrowRight size={9} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          )}
          </>
          )}
        </div>

        {/* Sticky CTA */}
        <aside className="lg:sticky lg:top-4 self-start space-y-3">
          <div className="bg-surface border border-border rounded-2xl p-5">
            <h3 className="text-base font-heading font-bold text-text-primary mb-1">Get connected</h3>
            <p className="text-xs text-text-muted mb-4">Send a quick brief and {vendor.name} will reach you within 48 hours.</p>
            <button onClick={() => setIntroOffering({})} data-testid="vendor-profile-intro" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: accent }}>
              <PaperPlaneTilt size={11} weight="bold" /> Request intro
            </button>
            <div className="text-[10px] text-text-muted text-center mt-2">Free. No obligation. Direct connection.</div>
          </div>
          {(vendor.website || vendor.contact_email || vendor.contact_phone) && (
            <div className="bg-surface border border-border rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-2">Contact</div>
              <div className="space-y-1.5 text-xs">
                {vendor.website && <a href={vendor.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary"><Globe size={11} weight="bold" /> {vendor.website}</a>}
                {vendor.contact_email && <div className="inline-flex items-center gap-2 text-text-secondary"><EnvelopeSimple size={11} weight="bold" /> {vendor.contact_email}</div>}
                {vendor.contact_phone && <div className="inline-flex items-center gap-2 text-text-secondary"><Phone size={11} weight="bold" /> {formatPhone(vendor.contact_phone)}</div>}
              </div>
            </div>
          )}
        </aside>
      </div>

      {introOffering && <IntroModal accent={accent} vendor={vendor} offering={introOffering} onClose={() => setIntroOffering(null)} />}
    </div>
  );
};

const IntroModal = ({ accent, vendor, offering, onClose }) => {
  const [offeringId, setOfferingId] = useState(offering?.id || '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/vendor-intros', { vendor_id: vendor.id, offering_id: offeringId || null, note });
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-start justify-center pt-16 p-4" onClick={onClose} data-testid="vendor-profile-intro-modal">
      <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-4"><h3 className="font-heading font-bold text-lg">Request intro · {vendor.name}</h3><button onClick={onClose}><X size={16} weight="bold" /></button></div>
        {done ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 rounded-full mx-auto mb-3 inline-flex items-center justify-center" style={{ backgroundColor: `${accent}1a`, color: accent }}><Check size={20} weight="bold" /></div>
            <p className="text-sm text-text-secondary mb-4">Intro requested! {vendor.name} will reach you within 48 hours.</p>
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: accent }}>Done</button>
          </div>
        ) : (
          <>
            {(vendor.offerings || []).length > 0 && (
              <div className="mb-3">
                <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Interested in</label>
                <select value={offeringId} onChange={(e) => setOfferingId(e.target.value)} data-testid="vendor-profile-intro-offering" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm">
                  <option value="">No specific offering</option>
                  {vendor.offerings.map((o) => <option key={o.id} value={o.id}>{o.title} — {o.currency} {(o.gross_price || 0).toLocaleString('en-IN')}</option>)}
                </select>
              </div>
            )}
            <div className="mb-4">
              <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Anything specific to share?</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="vendor-profile-intro-note" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
            </div>
            <button onClick={submit} disabled={busy} data-testid="vendor-profile-intro-submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: accent }}>
              <PaperPlaneTilt size={11} weight="bold" /> {busy ? 'Sending…' : 'Send request'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default VendorProfile;
